import { motion } from "framer-motion";
import { Stethoscope, HeartPulse, Users, Package, Megaphone, Database, Compass, Zap } from "lucide-react";
import { ChapterShell } from "../ChapterShell";
import { FadeUp, MaskedLines } from "../Reveal";
import { UROLOGY_PRODUCTS, HEADSHOT_FALLBACK } from "../../data/content";
import { imgFallback } from "../../utils";

const PILLARS = [
  { icon: Stethoscope, label: "Doctors" },
  { icon: HeartPulse, label: "Patients" },
  { icon: Users, label: "Sales Team" },
  { icon: Package, label: "Products" },
  { icon: Megaphone, label: "Marketing" },
  { icon: Database, label: "Data" },
  { icon: Compass, label: "Strategy" },
  { icon: Zap, label: "Execution" },
];

export const ChapterFour = () => (
  <ChapterShell
    n="04"
    id="chapter-4"
    era="2017 onward · Micro Labs"
    title="Building Urology from Scratch"
    quote="A business built from zero."
  >
    <div className="grid grid-cols-1 items-start gap-14 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <FadeUp>
          <p className="max-w-xl text-base leading-relaxed text-[#4A5568] md:text-lg">
            Credited with the launch of the <strong className="text-[#0A192F]">Urology Division at Micro Labs</strong> —
            leading sales enablement for a new enterprise sales team: product training, competitive intelligence,
            product messaging and white-board creation.
          </p>
        </FadeUp>

        <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.label}
              initial={{ opacity: 0, y: 44, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.09, type: "spring", stiffness: 120 }}
              className="glass flex flex-col items-center gap-3 rounded-2xl px-4 py-6 text-center"
            >
              <p.icon size={22} className="text-[#00BFA5]" />
              <span className="font-display text-[11px] font-bold uppercase tracking-[0.18em] text-[#0A192F]">
                {p.label}
              </span>
            </motion.div>
          ))}
        </div>

        <FadeUp delay={0.2} className="mt-12 flex flex-wrap items-end gap-10">
          <div>
            <p className="font-display text-6xl font-black tracking-tight text-[#0A192F] md:text-7xl">
              ₹10<span className="text-[#00BFA5]"> Cr</span>
            </p>
            <p className="mt-2 text-xs font-bold uppercase tracking-[0.22em] text-[#0A192F]/50">
              Business established from scratch — only from one zone
            </p>
          </div>
          <div className="glass rounded-2xl px-6 py-4">
            <p className="font-display text-3xl font-black text-[#0A192F]">7</p>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#0A192F]/50">
              New products launched
            </p>
          </div>
          <div className="glass rounded-2xl px-6 py-4">
            <p className="font-display text-3xl font-black text-[#0A192F]">
              Est. ₹24<span className="text-[#00BFA5]"> Cr</span>
            </p>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#0A192F]/50">
              In FY 26-27 by launching all India
            </p>
          </div>
        </FadeUp>
      </div>

      <div className="lg:col-span-5">
        <FadeUp delay={0.15} className="relative mx-auto max-w-md">
          <div className="absolute -inset-5 rounded-[2rem] bg-gradient-to-br from-[#00E5FF]/25 to-[#00BFA5]/20 blur-xl" />
          <div className="glass relative overflow-hidden rounded-[1.8rem] p-2">
            <img
              src="/assets/avatar-present.png"
              onError={imgFallback(HEADSHOT_FALLBACK)}
              alt="Presenting the Urology strategy"
              data-testid="chapter4-avatar"
              className="w-full rounded-[1.4rem] object-cover"
            />
          </div>
          <p className="mt-5 text-center text-[11px] font-bold uppercase tracking-[0.25em] text-[#0A192F]/45">
            Presenting the division strategy
          </p>
        </FadeUp>
      </div>
    </div>

    <div className="mt-20">
      <h3 className="font-display text-2xl font-black tracking-tight text-[#0A192F] md:text-3xl">
        <MaskedLines lines={["The Urology Portfolio — 17 launches"]} />
      </h3>
      <div className="mt-10 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
        {UROLOGY_PRODUCTS.map((p, i) => (
          <motion.div
            key={p.name}
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.55, delay: (i % 5) * 0.08 }}
            whileHover={{ y: -8, rotate: i % 2 === 0 ? -1.2 : 1.2 }}
            className="glass rounded-2xl px-5 py-6 text-center"
            data-testid={`product-card-${p.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
          >
            <p className="font-display text-base font-black tracking-wide text-[#0A192F]">{p.name}</p>
            <p className="mt-2 text-[10px] font-bold uppercase leading-relaxed tracking-[0.08em] text-[#00BFA5]">{p.tag}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </ChapterShell>
);
